import _ from 'lodash';
import { formatInTimeZone } from 'date-fns-tz';
import App from 'antd/lib/app';
import Modal from 'antd/lib/modal';
import Collapse from 'antd/lib/collapse';
import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { IMarketAward, MarketAwardStatus } from 'src/domain/caiso/cmri/interface';
import { CAISO_TZ } from 'src/domain/caiso';
import { currencyUSDFormatter } from 'src/domain/common/currencyFormatters';
import { usePrograms } from 'src/domain/program/usePrograms';
import { IProgram } from 'src/domain/program/interface';
import { useState } from 'react';
import { useCaisoMarketAwardsApprove, useCaisoMarketAwardsDeny } from 'src/domain/caiso/cmri/queries';
import List from 'antd/lib/list';
import Button from 'antd/lib/button';
import Empty from 'antd/lib/empty';
import { isMarketAwardExpired, makeUrlForMarketAwardDispatchEvent } from 'src/domain/caiso/cmri/market-award';
import Table, { TableProps } from 'antd/lib/table';
import './MarketAwardsViewModal.css';
import { add } from 'date-fns';

interface IMarketAwardsViewModalProps {
    awards: IMarketAward[];
    onClose?: (actionTaken: boolean) => void;
}

export const MarketAwardsViewModal = (props: IMarketAwardsViewModalProps) => {
    const { notification } = App.useApp();
    const { programs } = usePrograms();

    const validMarketAwards = _.orderBy(props.awards, ['start', 'asc']);
    const marketAward = validMarketAwards[0];
    const isExpired = isMarketAwardExpired(marketAward);

    const sites = _.orderBy(marketAward.sites, ['company.company_name', 'site_name']);
    const [dispatchSiteIds, setDispatchSiteIds] = useState<number[]>(marketAward.dispatch?.siteIds || sites.map(it => it.site_id));

    const caisoMarketAwardsDeny = useCaisoMarketAwardsDeny();
    const caisoMarketAwardsApprove = useCaisoMarketAwardsApprove();

    const onClose = props.onClose ? props.onClose : (actionTaken: boolean) => {};

    let viewMode: 'view' | 'approve' = 'view';
    if ([MarketAwardStatus.NEW, MarketAwardStatus.DENIED].includes(marketAward.status) && !isExpired) {
        viewMode = 'approve';
    }

    const canApprove = viewMode === 'approve' && sites.length > 0 && dispatchSiteIds.length > 0;
    const canDeny = viewMode === 'approve' && validMarketAwards.every(it => [MarketAwardStatus.NEW].includes(it.status));

    function onChangeSelection(siteId: number, checked: boolean) {
        const selectedSiteIds = checked ? _.uniq([...dispatchSiteIds, siteId]) : _.without([...dispatchSiteIds], siteId);

        console.info(`onChangeSelection siteId=${siteId}, checked=${checked}, all selected=${JSON.stringify(selectedSiteIds)}`);
        setDispatchSiteIds(selectedSiteIds);
    }

    async function onApprove() {
        const { dispatchId } = await caisoMarketAwardsApprove.mutateAsync({
            ids: validMarketAwards.map(it => it.id),
            dispatch: {
                siteIds: dispatchSiteIds,
            },
        });

        if (dispatchId) {
            const eventLink = makeUrlForMarketAwardDispatchEvent(dispatchId);
            notification.success({
                key: 'market-awards-approve-success',
                message: 'Market Awards Approved',
                description: (
                    <div>
                        View event{' '}
                        <a target="_blank" href={eventLink} style={{ textDecoration: 'underline' }} rel="noreferrer">
                            here
                        </a>
                    </div>
                ),
                duration: 10, // sec
            });
        }

        onClose(true);
    }

    async function onDeny() {
        await caisoMarketAwardsDeny.mutateAsync({ ids: validMarketAwards.map(item => item.id) });
        onClose(true);
    }

    const footerActions =
        viewMode === 'approve'
            ? [
                  <Button
                      key="market-awards-view-modal-deny"
                      type="text"
                      disabled={!canDeny}
                      loading={caisoMarketAwardsDeny.isLoading}
                      onClick={onDeny}
                  >
                      Deny
                  </Button>,
                  <Button
                      key="market-awards-view-modal-approve"
                      type="primary"
                      disabled={!canApprove}
                      loading={caisoMarketAwardsApprove.isLoading}
                      onClick={onApprove}
                  >
                      {validMarketAwards.length > 1 ? 'Combine and Create Event' : 'Create Event'}
                  </Button>,
              ]
            : null;

    return (
        <Modal
            title={validMarketAwards.length > 1 ? 'Combine Market Awards' : 'Market Award'}
            open
            destroyOnHidden
            maskClosable={viewMode === 'view'}
            keyboard={viewMode === 'view'}
            className="market-awards-view-modal"
            onCancel={() => onClose(false)}
            width="650px"
            footer={footerActions}
        >
            {viewMode === 'view' && <MarketAwardsDetailsView awards={validMarketAwards} programs={programs} />}
            {viewMode === 'approve' && <MarketAwardsDetailsApprove awards={validMarketAwards} programs={programs} />}

            <Collapse
                accordion
                size="small"
                defaultActiveKey={['sites']}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            >
                <Collapse.Panel id="sites-collapse-panel" key="sites" header={`Sites (${sites.length})`}>
                    {sites.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <div>No sites to dispatch.</div>
                                    <div>Please assign sites to your resource.</div>
                                </div>
                            }
                        />
                    ) : (
                        <List
                            className="market-award-sites-list"
                            size="small"
                            bordered={false}
                            dataSource={sites}
                            renderItem={site => {
                                return (
                                    <List.Item key={`site_${site.site_id}`} className="market-award-sites-item">
                                        <Checkbox
                                            disabled={viewMode === 'view'}
                                            checked={dispatchSiteIds.includes(site.site_id)}
                                            onChange={val => onChangeSelection(site.site_id, val.target.checked)}
                                        >
                                            <div className="market-award-sites-item-title">{`${site.company?.company_name}/${site.site_name}`}</div>
                                            <div className="market-award-sites-item-subtitle">
                                                ID: {site.san || ''} <span>&nbsp; {site.site_address} &nbsp;</span> Site#:{' '}
                                                {site.site_store_number ?? ''}
                                            </div>
                                        </Checkbox>
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                </Collapse.Panel>
            </Collapse>
        </Modal>
    );
};

function MarketAwardsDetailsApprove({ awards, programs }: { awards: IMarketAward[]; programs: IProgram[] }) {
    const firstAward = awards[0];

    const date = formatInTimeZone(firstAward.start, CAISO_TZ, 'MM/dd/yy');
    const startTime = formatInTimeZone(firstAward.start, CAISO_TZ, 'HH:mm:ss');
    const endTime = formatInTimeZone(firstAward.end, CAISO_TZ, 'HH:mm:ss');
    const mw = firstAward.mw;
    const price = firstAward.price == null ? '-' : currencyUSDFormatter.format(firstAward.price);
    const earnings = firstAward.earnings == null ? '-' : currencyUSDFormatter.format(firstAward.earnings);
    const status = _.capitalize(firstAward.status);
    const resourceName = firstAward.resourceName;

    const resourceMonth = getActiveResourceMonth(firstAward.resource);
    let programName = '-';

    if (resourceMonth?.program_id) {
        const resourceProgram = programs.find(p => p.program_id === resourceMonth.program_id);
        programName = resourceProgram ? resourceProgram.name : '-';
    }

    /** Combine several market awards */
    if (awards.length > 1) {
        const dataSource: { key: string; time?: string; mw?: string; price?: number | string; earnings?: number | string }[] = awards.map(it => {
            const startTime = formatInTimeZone(it.start, CAISO_TZ, 'HH:mm:ss');
            const endTime = formatInTimeZone(it.end, CAISO_TZ, 'HH:mm:ss');

            return {
                key: it.id,
                time: `${startTime} - ${endTime}`,
                mw: it.mw == null ? '' : `${it.mw}MW`,
                price: it.price == null ? '-' : currencyUSDFormatter.format(it.price),
                earnings: it.earnings == null ? '-' : currencyUSDFormatter.format(it.earnings),
            };
        });

        const totalEarnings = _.sum(_.compact(awards.map(it => it.earnings)));

        dataSource.push({
            key: 'total',
            time: undefined,
            mw: undefined,
            price: undefined,
            earnings: `Total: ${_.isNumber(totalEarnings) ? currencyUSDFormatter.format(totalEarnings) : '-'}`,
        });

        const columns: TableProps<any>['columns'] = [
            {
                title: 'Time',
                dataIndex: 'time',
                key: 'time',
                onCell: (record, rowIndex) => {
                    return {
                        colSpan: rowIndex === dataSource.length - 1 ? 3 : 1,
                    };
                },
            },
            {
                title: 'Awarded MW',
                dataIndex: 'mw',
                key: 'mw',
                onCell: (record, rowIndex) => {
                    return {
                        colSpan: rowIndex === dataSource.length - 1 ? 0 : 1,
                    };
                },
            },
            {
                title: 'Price',
                dataIndex: 'price',
                key: 'price',
                onCell: (record, rowIndex) => {
                    return {
                        colSpan: rowIndex === dataSource.length - 1 ? 0 : 1,
                    };
                },
            },
            {
                title: 'Earnings',
                dataIndex: 'earnings',
                key: 'earnings',
            },
        ];

        return (
            <div className="market-awards-details">
                <div className="market-awards-details-item with-border">
                    <div>
                        <div className="label">Date</div>
                        <div>{date}</div>
                    </div>
                    <div>
                        <div className="label">Resource</div>
                        <div>{resourceName}</div>
                    </div>
                    <div>
                        <div className="label">Associated Program</div>
                        <div>{programName}</div>
                    </div>
                </div>

                <div className="market-awards-details-item">
                    <Table dataSource={dataSource} columns={columns} style={{ width: '100%' }} bordered pagination={{ hideOnSinglePage: true }} />
                </div>

                <div className="market-awards-details-item">
                    <div>
                        <div className="label">Status</div>
                        <div>{status}</div>
                    </div>
                    <div></div>
                </div>
            </div>
        );
    }

    return (
        <div className="market-awards-details">
            <div className="market-awards-details-item with-border">
                <div>
                    <div className="label">Date</div>
                    <div>{date}</div>
                </div>
                <div>
                    <div className="label">Time</div>
                    <div>
                        {startTime} - {endTime}
                    </div>
                </div>
            </div>
            <div className="market-awards-details-item with-border">
                <div>
                    <div className="label">Resource</div>
                    <div>{resourceName}</div>
                </div>
                <div>
                    <div className="label">Associated Program</div>
                    <div>{programName}</div>
                </div>
            </div>
            <div className="market-awards-details-item with-border">
                <div>
                    <div className="label">Awarded MW</div>
                    <div>{mw}</div>
                </div>
                <div>
                    <div className="label">Price</div>
                    <div>{price}</div>
                </div>
                <div>
                    <div className="label">Earnings</div>
                    <div>{earnings}</div>
                </div>
            </div>
            <div className="market-awards-details-item">
                <div>
                    <div className="label">Status</div>
                    <div>{status}</div>
                </div>
                <div></div>
            </div>
        </div>
    );
}

function MarketAwardsDetailsView({ awards, programs }: { awards: IMarketAward[]; programs: IProgram[] }) {
    /** we view only one market award at a time */
    const firstAward = awards[0];

    const date = formatInTimeZone(firstAward.start, CAISO_TZ, 'MM/dd/yy');
    let startTime = formatInTimeZone(firstAward.start, CAISO_TZ, 'HH:mm:ss');
    let endTime = formatInTimeZone(firstAward.end, CAISO_TZ, 'HH:mm:ss');

    let eventStartTime: string = '';
    let eventEndTime: string = '';
    /** the dispatched event might be created from several combined market awards! */
    if (firstAward.dispatch?.event) {
        const { start: startUtc, duration } = firstAward.dispatch.event;
        const endUtc = add(startUtc, { minutes: duration });

        eventStartTime = formatInTimeZone(startUtc, CAISO_TZ, 'HH:mm:ss');
        eventEndTime = formatInTimeZone(endUtc, CAISO_TZ, 'HH:mm:ss');
    }

    const mw = firstAward.mw;
    const price = firstAward.price == null ? '-' : currencyUSDFormatter.format(firstAward.price);
    const earnings = firstAward.earnings == null ? '-' : currencyUSDFormatter.format(firstAward.earnings);
    const status = _.capitalize(firstAward.status);
    const resourceName = firstAward.resourceName;

    const resourceMonth = getActiveResourceMonth(firstAward.resource);
    let programName = '-';

    if (resourceMonth?.program_id) {
        const resourceProgram = programs.find(p => p.program_id === resourceMonth.program_id);
        programName = resourceProgram ? resourceProgram.name : '-';
    }

    const eventLink =
        firstAward.dispatch?.id && [MarketAwardStatus.APPROVED, MarketAwardStatus.AUTO].includes(firstAward.status)
            ? makeUrlForMarketAwardDispatchEvent(firstAward.dispatch.id)
            : null;

    return (
        <div className="market-awards-details">
            <div className="market-awards-details-item">
                <div>
                    <div className="label">Date</div>
                    <div>{date}</div>
                </div>
                <div>
                    <div className="label">Time</div>
                    <div>
                        {startTime} - {endTime}
                    </div>
                </div>
            </div>
            <div className="market-awards-details-item">
                <div>
                    <div className="label">Resource</div>
                    <div>{resourceName}</div>
                </div>
                <div>
                    <div className="label">Associated Program</div>
                    <div>{programName}</div>
                </div>
            </div>
            <div className="market-awards-details-item">
                <div>
                    <div className="label">Awarded MW</div>
                    <div>{mw}</div>
                </div>
                <div>
                    <div className="label">Price</div>
                    <div>{price}</div>
                </div>
                <div>
                    <div className="label">Earnings</div>
                    <div>{earnings}</div>
                </div>
            </div>
            <div className="market-awards-details-item">
                <div>
                    <div className="label">Status</div>
                    <div>{status}</div>
                </div>
                <div>
                    <div className="label">{eventLink ? 'Event' : ''}</div>
                    <div>
                        {eventLink ? (
                            <a title="Go to Events page" className="event-link" target="_blank" href={eventLink} rel="noreferrer">
                                {date} {eventStartTime} - {eventEndTime}
                            </a>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function getActiveResourceMonth(resource: IMarketAward['resource']) {
    if (!resource) return null;

    const formattedMonth = formatInTimeZone(new Date(), CAISO_TZ, 'yyyy/MM');
    const activeMonth = resource.months.find(it => it.month === formattedMonth && it.mw > 0);
    if (!activeMonth) return null;

    return activeMonth;
}
